import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * Media alt-text naming system:
 *
 * Universal images:
 * always
 * always-model
 * always-flatlay
 * always-detail
 *
 * Color-specific images:
 * color-blue-front
 * color-blue-back
 * color-red-front
 * color-red-back
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal,
    });

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    /*
     * Run immediately so filtering happens before the customer interacts
     * with the gallery.
     */
    this.#applyMediaFiltering();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Normalizes an alt-text/group value.
   *
   * @param {string | undefined | null} value
   * @returns {string}
   */
  #normalizeGroup(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');
  }

  /**
   * Returns true when the media should always remain visible.
   *
   * Anything not beginning with "color-" is treated as universal.
   * This prevents older images with normal descriptive alt text from
   * disappearing unexpectedly.
   *
   * @param {string} group
   * @returns {boolean}
   */
  #isUniversalGroup(group) {
    return group.startsWith('always') || !group.startsWith('color-');
  }

  /**
   * Returns true when the group belongs to the selected color.
   *
   * @param {string} group
   * @param {string} selectedColor
   * @returns {boolean}
   */
  #matchesSelectedColor(group, selectedColor) {
    if (!selectedColor) return false;

    return group.startsWith(`color-${selectedColor}-`);
  }

  /**
   * Determines whether a media item should remain visible.
   *
   * @param {string} group
   * @param {string} selectedColor
   * @param {boolean} hasColorSelection
   * @returns {boolean}
   */
  #shouldKeepMedia(group, selectedColor, hasColorSelection) {
    if (this.#isUniversalGroup(group)) return true;

    if (!hasColorSelection) return false;

    return this.#matchesSelectedColor(group, selectedColor);
  }

  /**
   * Filters the slideshow, desktop grid, thumbnails, dots and zoom gallery.
   */
  #applyMediaFiltering() {
    const selectedColor = this.#normalizeGroup(this.dataset.selectedColor);

    /*
     * On the initial product-page visit, Shopify may internally choose its
     * first variant even though the customer has not chosen a color.
     *
     * A variant query parameter means the customer selected a variant or
     * arrived through a direct variant link.
     */
    const url = new URL(window.location.href);

    const hasColorSelection =
      this.dataset.forceSelectedColor === 'true' ||
      url.searchParams.has('variant');

    const allSlides = Array.from(
      this.querySelectorAll('slideshow-slide[data-media-group]')
    );

    if (!allSlides.length) return;

    const slideVisibility = allSlides.map((slide) => {
      const group = this.#normalizeGroup(slide.dataset.mediaGroup);

      return {
        slide,
        group,
        keep: this.#shouldKeepMedia(
          group,
          selectedColor,
          hasColorSelection
        ),
      };
    });

    /*
     * Prevent an empty gallery on products that have no universal images yet.
     * In that case, retain the first gallery image.
     */
    if (!slideVisibility.some((item) => item.keep)) {
      slideVisibility[0].keep = true;
    }

    /*
     * Controls must be mapped before slides are removed because their
     * original indexes correspond to the original slide order.
     */
    const thumbnailGroups = Array.from(
      this.querySelectorAll(
        'slideshow-controls .slideshow-controls__thumbnails'
      )
    );

    const dotGroups = Array.from(
      this.querySelectorAll(
        'slideshow-controls .slideshow-controls__dots'
      )
    );

    for (const thumbnailGroup of thumbnailGroups) {
      const buttons = Array.from(
        thumbnailGroup.querySelectorAll(
          '.slideshow-controls__thumbnail'
        )
      );

      slideVisibility.forEach((item, index) => {
        if (!item.keep) {
          buttons[index]?.remove();
        }
      });
    }

    for (const dotGroup of dotGroups) {
      const dots = Array.from(dotGroup.querySelectorAll('li'));

      slideVisibility.forEach((item, index) => {
        if (!item.keep) {
          dots[index]?.remove();
        }
      });
    }

    /*
     * Remove unrelated carousel slides.
     */
    slideVisibility.forEach((item) => {
      if (!item.keep) {
        item.slide.remove();
      }
    });

    /*
     * Filter the desktop grid.
     */
    const gridItems = Array.from(
      this.querySelectorAll(
        '.media-gallery__grid > [data-media-group]'
      )
    );

    for (const gridItem of gridItems) {
      const group = this.#normalizeGroup(gridItem.dataset.mediaGroup);
      const keep = this.#shouldKeepMedia(
        group,
        selectedColor,
        hasColorSelection
      );

      if (!keep) {
        gridItem.remove();
      }
    }

    /*
     * Filter zoom thumbnails.
     */
    const zoomThumbnails = Array.from(
      this.querySelectorAll(
        '.dialog-thumbnails-list__thumbnail[data-media-group]'
      )
    );

    for (const thumbnail of zoomThumbnails) {
      const group = this.#normalizeGroup(thumbnail.dataset.mediaGroup);
      const keep = this.#shouldKeepMedia(
        group,
        selectedColor,
        hasColorSelection
      );

      if (!keep) {
        thumbnail.remove();
      }
    }

    /*
     * Filter full-size zoom images.
     */
    const zoomMediaItems = Array.from(
      this.querySelectorAll(
        '.dialog-zoomed-gallery > [data-media-group]'
      )
    );

    for (const mediaItem of zoomMediaItems) {
      const group = this.#normalizeGroup(mediaItem.dataset.mediaGroup);
      const keep = this.#shouldKeepMedia(
        group,
        selectedColor,
        hasColorSelection
      );

      if (!keep) {
        mediaItem.remove();
      }
    }

    this.#reindexGalleryControls();
    this.#updateCounters();
    this.#selectStartingMedia(selectedColor, hasColorSelection);
  }

  /**
   * Reindexes slideshow, zoom and grid controls after unrelated media
   * has been removed.
   */
  #reindexGalleryControls() {
    const thumbnailGroups = Array.from(
      this.querySelectorAll(
        'slideshow-controls .slideshow-controls__thumbnails'
      )
    );

    for (const thumbnailGroup of thumbnailGroups) {
      const thumbnails = Array.from(
        thumbnailGroup.querySelectorAll(
          '.slideshow-controls__thumbnail'
        )
      );

      thumbnails.forEach((thumbnail, index) => {
        thumbnail.setAttribute('on:click', `/select/${index}`);
        thumbnail.setAttribute(
          'aria-label',
          `Slide ${index + 1} of ${thumbnails.length}`
        );

        thumbnail.setAttribute(
          'aria-selected',
          index === 0 ? 'true' : 'false'
        );
      });
    }

    const dotGroups = Array.from(
      this.querySelectorAll(
        'slideshow-controls .slideshow-controls__dots'
      )
    );

    for (const dotGroup of dotGroups) {
      const dotButtons = Array.from(dotGroup.querySelectorAll('button'));

      dotButtons.forEach((button, index) => {
        button.setAttribute('on:click', `/select/${index}`);
        button.setAttribute(
          'aria-label',
          `Slide ${index + 1} of ${dotButtons.length}`
        );

        button.setAttribute(
          'aria-selected',
          index === 0 ? 'true' : 'false'
        );
      });
    }

    /*
     * Reindex the main slideshow’s zoom-opening commands.
     */
    const slides = Array.from(
      this.querySelectorAll('slideshow-slide[data-media-group]')
    );

    slides.forEach((slide, index) => {
      const command = slide.getAttribute('on:click');

      if (command?.includes('/open/')) {
        slide.setAttribute(
          'on:click',
          command.replace(/\/open\/\d+$/, `/open/${index}`)
        );
      }
    });

    /*
     * Reindex desktop-grid zoom buttons.
     */
    const gridItems = Array.from(
      this.querySelectorAll(
        '.media-gallery__grid > [data-media-group]'
      )
    );

    gridItems.forEach((gridItem, index) => {
      const zoomButton = gridItem.querySelector(
        '.product-media-container__zoom-button'
      );

      if (!zoomButton) return;

      zoomButton.setAttribute('on:click', `/zoom/${index}`);
      zoomButton.setAttribute(
        'on:pointerenter',
        `/preloadImage/${index}`
      );
    });

    /*
     * Reindex zoom-dialog thumbnails.
     */
    const zoomThumbnails = Array.from(
      this.querySelectorAll(
        '.dialog-thumbnails-list__thumbnail[data-media-group]'
      )
    );

    zoomThumbnails.forEach((thumbnail, index) => {
      thumbnail.setAttribute(
        'on:click',
        `/handleThumbnailClick/${index}`
      );

      thumbnail.setAttribute(
        'on:pointerenter',
        `/handleThumbnailPointerEnter/${index}`
      );

      thumbnail.setAttribute(
        'aria-selected',
        index === 0 ? 'true' : 'false'
      );
    });
  }

  /**
   * Updates slideshow counters after images are filtered.
   */
  #updateCounters() {
    const visibleSlideCount = this.querySelectorAll(
      'slideshow-slide[data-media-group]'
    ).length;

    const counters = Array.from(
      this.querySelectorAll('.slideshow-controls__counter')
    );

    for (const counter of counters) {
      counter.innerHTML = `
        <span ref="current">1</span>
        <span class="slash">/</span>
        ${visibleSlideCount}
      `;
    }
  }

  /**
   * Selects the appropriate first image.
   *
   * Initial load:
   * First universal/model/flat-lay image.
   *
   * After color selection:
   * Selected color's front image.
   *
   * @param {string} selectedColor
   * @param {boolean} hasColorSelection
   */
  #selectStartingMedia(selectedColor, hasColorSelection) {
    requestAnimationFrame(() => {
      const slideshow = this.querySelector('slideshow-component');

      if (!slideshow || typeof slideshow.select !== 'function') return;

      const visibleSlides = Array.from(
        this.querySelectorAll('slideshow-slide[data-media-group]')
      );

      if (!visibleSlides.length) return;

      let targetSlide;

      if (hasColorSelection && selectedColor) {
        const desiredFrontGroup = `color-${selectedColor}-front`;

        targetSlide = visibleSlides.find((slide) => {
          const group = this.#normalizeGroup(slide.dataset.mediaGroup);
          return group === desiredFrontGroup;
        });

        /*
         * If there is no explicitly labeled front image, use the first
         * image belonging to the selected color.
         */
        if (!targetSlide) {
          targetSlide = visibleSlides.find((slide) => {
            const group = this.#normalizeGroup(slide.dataset.mediaGroup);
            return this.#matchesSelectedColor(group, selectedColor);
          });
        }
      }

      /*
       * On initial load, or if no selected-color image was found,
       * use the first remaining universal image.
       */
      if (!targetSlide) {
        targetSlide = visibleSlides.find((slide) => {
          const group = this.#normalizeGroup(slide.dataset.mediaGroup);
          return this.#isUniversalGroup(group);
        });
      }

      targetSlide ||= visibleSlides[0];

      const slideId = targetSlide.getAttribute('slide-id');

      if (slideId) {
        slideshow.select(
          { id: slideId },
          undefined,
          { animate: false }
        );
      } else {
        slideshow.select(0, undefined, { animate: false });
      }
    });
  }

  /**
   * Replaces the gallery after Shopify updates a variant.
   *
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    /*
     * Guarantees that a user-triggered selection is treated as active,
     * even if the browser URL has not finished updating yet.
     */
    newMediaGallery.dataset.forceSelectedColor = 'true';

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles zoom-media selection.
   *
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, {
      animate: false,
    });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index
   * @param {PointerEvent} event
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   *
   * @param {number} index
   */
  preloadImage(index) {
    const zoomDialogMedia =
      this.refs.zoomDialogComponent?.refs.media[index];

    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(
      zoomDialogMedia
    );
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}